import {
  recordAuthAgeGate,
  recordAuthCpfValidationError,
  recordAuthFunnelBridgeInvalid,
  recordAuthFunnelError,
  recordAuthFunnelEvent,
  recordAuthStepDuration,
} from '@jusbrasil-web/core-next/prometheus'
import {
  type AuthFunnelStatus,
  type AuthFunnelStep,
  makeAuthFunnelLabelGuard,
} from '@jusbrasil-web/shared-auth-flows'

// Steps and statuses are single-sourced in `@jusbrasil-web/shared-auth-flows`: the
// emitters span `scope:auth` (signup modal, CPF form) and `scope:landing-pages`
// (CPF-monitor LP), and NX forbids those scopes from depending on each other.
export {
  AUTH_FUNNEL_STATUSES,
  AUTH_FUNNEL_STEPS,
  isAuthFunnelStatus,
  isAuthFunnelStep,
} from '@jusbrasil-web/shared-auth-flows'

export const AUTH_FUNNEL_BRIDGE_INVALID_REASONS = [
  'origin',
  'method',
  'parse',
  'label',
  'size',
] as const
export type AuthFunnelBridgeInvalidReason = (typeof AUTH_FUNNEL_BRIDGE_INVALID_REASONS)[number]

export const AUTH_FUNNEL_ERROR_KINDS = [
  'validation',
  'network',
  'backend',
  'timeout',
  'unknown',
] as const
export type AuthFunnelErrorKind = (typeof AUTH_FUNNEL_ERROR_KINDS)[number]

export const AUTH_CPF_VALIDATION_REASONS = [
  'invalid_format',
  'invalid_checksum',
  'blocked_list',
  'empty',
] as const
export type AuthCpfValidationReason = (typeof AUTH_CPF_VALIDATION_REASONS)[number]

export const AUTH_AGE_GATE_ORIGINS = ['birthdate_change', 'submit'] as const
export type AuthAgeGateOrigin = (typeof AUTH_AGE_GATE_ORIGINS)[number]

export const AUTH_AGE_GATE_OUTCOMES = ['allowed', 'blocked'] as const
export type AuthAgeGateOutcome = (typeof AUTH_AGE_GATE_OUTCOMES)[number]

export const isAuthFunnelErrorKind = makeAuthFunnelLabelGuard(AUTH_FUNNEL_ERROR_KINDS)
export const isAuthCpfValidationReason = makeAuthFunnelLabelGuard(AUTH_CPF_VALIDATION_REASONS)
export const isAuthAgeGateOrigin = makeAuthFunnelLabelGuard(AUTH_AGE_GATE_ORIGINS)
export const isAuthAgeGateOutcome = makeAuthFunnelLabelGuard(AUTH_AGE_GATE_OUTCOMES)

export const recordAuthFunnelStep = async (step: AuthFunnelStep, status: AuthFunnelStatus) => {
  await recordAuthFunnelEvent({ kind: 'event', step, status })
}

export const recordAuthFunnelInvalidBridgeRequest = async (
  reason: AuthFunnelBridgeInvalidReason
) => {
  await recordAuthFunnelBridgeInvalid(reason)
}

export const recordAuthFunnelErrorEvent = async (
  step: AuthFunnelStep,
  kind: AuthFunnelErrorKind
) => {
  await recordAuthFunnelError({ step, kind })
}

export const recordAuthStepDurationSeconds = async (
  step: AuthFunnelStep,
  status: AuthFunnelStatus,
  seconds: number
) => {
  await recordAuthStepDuration({ step, status }, seconds)
}

export const recordAuthCpfValidationErrorEvent = async (reason: AuthCpfValidationReason) => {
  await recordAuthCpfValidationError({ reason })
}

export const recordAuthAgeGateEvent = async (
  origin: AuthAgeGateOrigin,
  outcome: AuthAgeGateOutcome
) => {
  await recordAuthAgeGate({ origin, outcome })
}
